"use client";

import { useTranslation } from "react-i18next";
import {
    formatSearchParamsToString,
    AuthContext,
    useRuntimeConfig,
    useSearchParamsQuery,
} from "@finbackoffice/site-core";
import { DeviceType } from "@finbackoffice/enums";
import AccountCompact from "components/account/menu/compact/AccountCompact";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import {
    ModalStyleTypeEnum,
    ModalBorderRadiusEnum,
} from "components/base/modal/constants/modal-config";
import Modal from "components/base/modal/Modal";
import {
    ConfigContextV2,
    IModalForwardRefPropsV2,
    ModalsContextV2,
    ActiveSportGameContext,
    MultiBetBonusProviderV2,
    NotificationContext,
    UserAccountContextV2,
} from "contexts";
import { usePathname, useRouter } from "next/navigation";
import {
    FC,
    RefObject,
    useRef,
    useEffect,
    useMemo,
    useContext,
    useCallback,
    SyntheticEvent,
} from "react";
import { ModalTypes, RouterQuery } from "utils/constants";
import dynamic from "next/dynamic";
import PreloadComponent from "components/base/preload-component/PreloadComponent";
import { INotificationResponse } from "@finbackoffice/clientbff-client";
import SiteNotificationsModal from "components/site-notifications-modal/SiteNotificationsModal";
import { ModalCloseButtonType } from "components/base/modal/components/ModalCloseButton";
import { WalletLayoutEnum } from "@finbackoffice/fe-core";
import classNames from "classnames";
import { roboto } from "fonts/roboto";
import Loading from "components/base/loading/Loading";
import { useDevice, usePersonalDetailsVerificationConfig } from "hooks";
import styles from "./global-modals-container.module.sass";

const PersonalDetailsVerificationModal = dynamic(
    () =>
        import("components/account/personal-details-verification/PersonalDetailsVerificationModal"),
    {
        // loading: () => <PreloadComponent style={{ height: 422, margin: 0, border: 0 }} />,
        ssr: false,
    },
);

const LoginPanel = dynamic(() => import("components/account/login-panel/LoginPanel"), {
    loading: () => <PreloadComponent style={{ height: 450, margin: 0, border: 0 }} />,
    ssr: false,
});

const InactivityModal = dynamic(() => import("components/account/inactivity/InactivityModal"), {
    loading: () => <PreloadComponent style={{ margin: 0, border: 0 }} />,
    ssr: false,
});

const Game = dynamic(() => import("components/sport/base/game/Game"), {
    ssr: false,
});

const Verification = dynamic(() => import("components/account/verification/Verification"), {
    ssr: false,
});

const BetSlip = dynamic(() => import("components/sport/betslip/BetSlip"), {
    ssr: false,
});

const PixWalletWidget = dynamic(
    () =>
        import("components/base/wallet/pix-wallet/PixWalletWidget").then(
            (module) => module.PixWalletWidget,
        ),
    {
        loading: () => <Loading />,
        ssr: false,
    },
);

const TolaWalletWidget = dynamic(
    () =>
        import("components/base/wallet/tola-wallet/TolaWalletWidget").then(
            (module) => module.TolaWalletWidget,
        ),
    {
        loading: () => <Loading />,
        ssr: false,
    },
);

const MultiWalletWidget = dynamic(
    () =>
        import("components/base/wallet/multi-wallet/MultiWalletWidget").then(
            (module) => module.MultiWalletWidget,
        ),
    {
        loading: () => <Loading />,
        ssr: false,
    },
);

const MultiWalletWidgetPro = dynamic(
    () =>
        import("components/base/wallet/multi-wallet-pro/MultiWalletWidgetPro").then(
            (module) => module.MultiWalletWidgetPro,
        ),
    {
        loading: () => <Loading />,
        ssr: false,
    },
);

type Props = {
    notifications: INotificationResponse[];
    deviceType: DeviceType;
};

const GlobalModalsContainer: FC<Props> = ({ notifications, deviceType }) => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const router = useRouter();
    const { t } = useTranslation();
    const pathname = usePathname();
    const { query } = useSearchParamsQuery();
    const { gameId, type: modalType } = query;
    const {
        isSportMultiBetBonusesEnabled,
        isPhoneVerificationRequired,
        isEmailVerificationRequired,
        isIdVerificationRequired,
    } = useContext(ConfigContextV2);
    const {
        accountModalRef,
        loginModalRef,
        personalDetailsVerificationModalRef,
        verificationModalRef,
        betSlipModalRef,
        setCurrentModal,
        currentModal,
        walletModalRef,
    } = useContext(ModalsContextV2);
    const { activeGameState, setActiveGameState } = useContext(ActiveSportGameContext);
    const { isUserLoggedIn } = useContext(AuthContext);
    const { userProfileStatus, isKycVerified, isEmailVerified, isPhoneVerified, loadProfile } =
        useContext(UserAccountContextV2);
    const { notice } = useContext(NotificationContext);
    const { needToVerify } = usePersonalDetailsVerificationConfig();
    const { isMobileDevice } = useDevice();

    const gameModalRef: RefObject<IModalForwardRefPropsV2 | null> = useRef(null);

    const modalQueueRef = useRef<Array<() => void>>([]);
    const isProcessingQueueRef = useRef(false);

    const processModalQueue = useCallback(() => {
        if (isProcessingQueueRef.current || modalQueueRef.current.length === 0) {
            return;
        }

        isProcessingQueueRef.current = true;
        const nextModal = modalQueueRef.current.shift();
        if (nextModal) {
            nextModal();
        }
    }, []);

    const handleModalQueueClose = useCallback(() => {
        isProcessingQueueRef.current = false;
        processModalQueue();
    }, [processModalQueue]);

    useEffect(() => {
        if (userProfileStatus) {
            modalQueueRef.current = [];

            if (modalType === ModalTypes.ACCOUNT) {
                modalQueueRef.current.push(() => {
                    accountModalRef.current?.open();
                });
            } else if (
                (isEmailVerificationRequired && !isEmailVerified) ||
                (isPhoneVerificationRequired && !isPhoneVerified)
            ) {
                modalQueueRef.current.push(() => {
                    verificationModalRef.current?.open();
                });
            } else if (isIdVerificationRequired && !isKycVerified) {
                modalQueueRef.current.push(() => {
                    notice(
                        {
                            type: "error",
                            title: t("verification_subHeading"),
                            message: t("verification_id_description"),
                        },
                        "modal",
                        handleModalQueueClose,
                    );
                });
            }

            if (COMMON_SITE_CONFIGS.verification.enable && needToVerify) {
                modalQueueRef.current.push(() => {
                    personalDetailsVerificationModalRef.current?.open();
                });
            }

            // Start processing the queue
            processModalQueue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userProfileStatus]);

    useEffect(() => {
        if (
            [
                ModalTypes.BET_SLIP,
                ModalTypes.ACCOUNT,
                ModalTypes.LOGIN,
                ModalTypes.SIGNUP,
                ModalTypes.SETTINGS,
                ModalTypes.WALLET,
            ].includes(currentModal || "")
        ) {
            setCurrentModal(null);
        } else if (gameModalRef.current?.isOpened) {
            void gameModalRef.current.close();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    useEffect(() => {
        if (modalType === RouterQuery.Login && loginModalRef.current) {
            loginModalRef.current.open();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalType]);

    useEffect(() => {
        if (activeGameState.gameId && !gameModalRef.current?.isOpened) {
            gameModalRef.current?.open();
        }
    }, [activeGameState.gameId]);

    useEffect(() => {
        if (!gameId && activeGameState.gameId) {
            void gameModalRef.current?.close();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameId]);

    const renderProfileModal = useMemo(
        () =>
            isMobileDevice ? (
                <ErrorBoundary name={AccountCompact.name}>
                    <Modal
                        ref={accountModalRef}
                        styleConfigType={ModalStyleTypeEnum.DetailsCompact}
                        styleClass={styles.accountModalCompact}
                        type={ModalTypes.ACCOUNT}
                        onClose={handleModalQueueClose}
                        animateVariant="bottom">
                        <AccountCompact />
                    </Modal>
                </ErrorBoundary>
            ) : null,
        [accountModalRef, handleModalQueueClose, isMobileDevice],
    );

    const renderLoginModal = useMemo(
        () => (
            <ErrorBoundary name={LoginPanel.name}>
                <Modal
                    ref={loginModalRef}
                    styleClass={styles.loginModalCompact}
                    styleConfigType={ModalStyleTypeEnum.Login}
                    type={ModalTypes.LOGIN}
                    closeButton={deviceType !== DeviceType.Mobile}
                    maskClosable={false}
                    animateVariant={deviceType === DeviceType.Mobile ? "bottom" : "opacity"}>
                    <LoginPanel />
                </Modal>
            </ErrorBoundary>
        ),
        [loginModalRef, deviceType],
    );

    const renderBetSlipModal = (
        <Modal
            ref={betSlipModalRef}
            type={ModalTypes.BET_SLIP}
            styleConfigType={ModalStyleTypeEnum.Betslip}
            animateVariant="bottom"
            styleClass={styles.betslipModal}>
            <BetSlip />
        </Modal>
    );

    const onWalletClose = useCallback(async () => {
        if (isUserLoggedIn) {
            await loadProfile();
        }
    }, [loadProfile, isUserLoggedIn]);

    const onSportGameModalClose = useCallback(
        (e?: SyntheticEvent | KeyboardEvent) => {
            if (e) {
                const q = { ...query };
                delete q.gameId;
                router.replace(`${pathname}?${formatSearchParamsToString(q)}`);
            }

            setActiveGameState({
                tournament: null,
                gameId: null,
            });
        },
        [pathname, query, router, setActiveGameState],
    );

    const renderWallet = useMemo(() => {
        switch (COMMON_SITE_CONFIGS.wallet.type) {
            case WalletLayoutEnum.Pix:
                return (
                    <ErrorBoundary name={PixWalletWidget.name}>
                        <Modal
                            ref={walletModalRef}
                            styleClass={classNames(styles.walletPixModal, roboto.variable)}
                            styleConfigType={ModalStyleTypeEnum.WalletPix}
                            closeButtonVariant={ModalCloseButtonType.wallet}
                            type={ModalTypes.WALLET}
                            animateVariant="opacity"
                            maskClosable={false}
                            alignMiddle={true}
                            closeButton>
                            <PixWalletWidget />
                        </Modal>
                    </ErrorBoundary>
                );
            case WalletLayoutEnum.Tola:
                return (
                    <ErrorBoundary name={TolaWalletWidget.name}>
                        <Modal
                            ref={walletModalRef}
                            styleClass={classNames(styles.walletTolaModal, roboto.variable)}
                            styleConfigType={ModalStyleTypeEnum.WalletTola}
                            closeButtonVariant={ModalCloseButtonType.wallet}
                            closeButton
                            type={ModalTypes.WALLET}
                            animateVariant="opacity"
                            maskClosable={false}>
                            <TolaWalletWidget />
                        </Modal>
                    </ErrorBoundary>
                );
            case WalletLayoutEnum.MultiWallet:
                return (
                    <ErrorBoundary name={MultiWalletWidget.name}>
                        <Modal
                            ref={walletModalRef}
                            styleClass={classNames(styles.multiWalletModal, roboto.variable)}
                            styleConfigType={ModalStyleTypeEnum.WalletMulti}
                            closeButtonVariant={ModalCloseButtonType.wallet}
                            onClose={onWalletClose}
                            closeButton
                            alignMiddle={true}
                            type={ModalTypes.WALLET}
                            animateVariant="opacity"
                            maskClosable={false}>
                            <MultiWalletWidget />
                        </Modal>
                    </ErrorBoundary>
                );
            default:
                return (
                    <ErrorBoundary name={MultiWalletWidget.name}>
                        <Modal
                            ref={walletModalRef}
                            styleClass={classNames(styles.multiWalletProModal, roboto.variable)}
                            styleConfigType={ModalStyleTypeEnum.WalletMultiPro}
                            onClose={onWalletClose}
                            closeButton
                            alignMiddle={true}
                            type={ModalTypes.WALLET}
                            animateVariant="opacity"
                            maskClosable={false}>
                            <MultiWalletWidgetPro />
                        </Modal>
                    </ErrorBoundary>
                );
        }
    }, [COMMON_SITE_CONFIGS.wallet.type, onWalletClose, walletModalRef]);

    return (
        <>
            {COMMON_SITE_CONFIGS.detectInactivity.enable && <InactivityModal />}
            {isUserLoggedIn ? (
                <>
                    {renderProfileModal}
                    {renderWallet}
                </>
            ) : (
                renderLoginModal
            )}

            <Modal
                ref={gameModalRef}
                type={ModalTypes.GAME}
                styleConfigType={ModalStyleTypeEnum.Game}
                closeButtonVariant={ModalCloseButtonType.square}
                animateVariant="bottom"
                closeButton
                priority
                styleClass={styles.game}
                onClose={onSportGameModalClose}>
                <div className={styles.container}>
                    {activeGameState.gameId && (
                        <Game id={parseInt(activeGameState.gameId)} variant="modal" />
                    )}
                </div>
            </Modal>
            <Modal
                ref={verificationModalRef}
                styleClass={styles.userVerificationModal}
                styleConfigType={ModalStyleTypeEnum.Verification}
                type={ModalTypes.VERIFICATION}
                closeButton
                maskClosable={false}
                onClose={handleModalQueueClose}
                animateVariant="opacity">
                <Verification />
            </Modal>
            {COMMON_SITE_CONFIGS.verification.enable && (
                <Modal
                    ref={personalDetailsVerificationModalRef}
                    styleClass={styles.personalDetailsVerificationModal}
                    styleConfigType={ModalStyleTypeEnum.Verification}
                    customStyleConfig={{ borderRadius: ModalBorderRadiusEnum.large }}
                    type={ModalTypes.PERSONAL_DETAILS_VERIFICATION}
                    closeButton
                    maskClosable={false}
                    onClose={handleModalQueueClose}
                    animateVariant="opacity">
                    <PersonalDetailsVerificationModal />
                </Modal>
            )}
            {isSportMultiBetBonusesEnabled ? (
                <MultiBetBonusProviderV2>{renderBetSlipModal}</MultiBetBonusProviderV2>
            ) : (
                renderBetSlipModal
            )}
            {notifications.length > 0 && (
                <ErrorBoundary name={SiteNotificationsModal.name}>
                    <SiteNotificationsModal notifications={notifications} />
                </ErrorBoundary>
            )}
        </>
    );
};

export default GlobalModalsContainer;
